export const EMPTY_SETUP_FILTERS = {
  campaign: null,
  designation: null,
  office: null,
  status: null,
};

export const SETUP_STATUS_OPTIONS = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

const FILTER_CHIP_LABELS = {
  campaign: "Campaign",
  designation: "Designation",
  office: "Office",
  status: "Status",
};

const SORT_COLUMN_TO_URL = {
  title: "title",
  office: "office",
  status: "status",
};

const SORT_URL_TO_COLUMN = Object.fromEntries(
  Object.entries(SORT_COLUMN_TO_URL).map(([columnId, urlKey]) => [urlKey, columnId])
);

export function hasActiveSetupFilters(filters) {
  if (!filters) return false;
  return Object.values(filters).some((entry) => entry?.value);
}

export function parseSearchParamsToSetupFilters(searchParams, labelResolver = {}) {
  const next = { ...EMPTY_SETUP_FILTERS };

  const assign = (key, paramName, formatLabel) => {
    const raw = searchParams.get(paramName);
    if (!raw) return;
    const label =
      labelResolver[key]?.(raw) ||
      (typeof formatLabel === "function" ? formatLabel(raw) : raw);
    next[key] = { value: raw, label };
  };

  assign("campaign", "campaign");
  assign("designation", "designation");
  assign("office", "office");
  assign("status", "status", (raw) => {
    const match = SETUP_STATUS_OPTIONS.find((opt) => opt.value === raw);
    return match?.label || raw;
  });

  return next;
}

export function buildSetupSearchParamsFromState({ filters, sort, search }) {
  const params = new URLSearchParams();

  if (filters?.campaign?.value) params.set("campaign", String(filters.campaign.value));
  if (filters?.designation?.value) {
    params.set("designation", String(filters.designation.value));
  }
  if (filters?.office?.value) params.set("office", String(filters.office.value));
  if (filters?.status?.value) params.set("status", String(filters.status.value));

  if (sort?.columnId && sort?.direction) {
    const sortBy = SORT_COLUMN_TO_URL[sort.columnId];
    if (sortBy) {
      params.set("sortBy", sortBy);
      params.set("sortDir", sort.direction);
    }
  }

  if (search && String(search).trim()) {
    params.set("q", String(search).trim());
  }

  return params;
}

export function sortFromSetupUrlParams(searchParams) {
  const sortBy = searchParams.get("sortBy");
  const sortDir = searchParams.get("sortDir");
  if (!sortBy || !sortDir) return null;
  const columnId = SORT_URL_TO_COLUMN[sortBy];
  if (!columnId) return null;
  if (sortDir !== "asc" && sortDir !== "desc") return null;
  return { columnId, direction: sortDir };
}

export function buildSetupActiveFilterChips(filters, sort) {
  const chips = [];

  Object.entries(FILTER_CHIP_LABELS).forEach(([key, prefix]) => {
    const entry = filters?.[key];
    if (entry?.value) {
      chips.push({
        id: `filter-${key}`,
        type: "filter",
        key,
        label: `${prefix}: ${entry.label || entry.value}`,
      });
    }
  });

  if (sort?.columnId && sort?.direction) {
    const labels = {
      title: "Title",
      office: "Office",
      status: "Status",
    };
    chips.push({
      id: "sort",
      type: "sort",
      key: "sort",
      label: `Sort: ${labels[sort.columnId] || sort.columnId} (${sort.direction})`,
    });
  }

  return chips;
}

export function resolveSetupFilterLabels(filters, lists = {}) {
  const next = { ...EMPTY_SETUP_FILTERS, ...filters };

  const resolveEntity = (entry, list, labelKeys) => {
    if (!entry?.value) return null;
    const match = (list || []).find((item) => String(item.id) === String(entry.value));
    if (!match) return entry;
    const label =
      labelKeys.map((key) => match[key]).find(Boolean) || entry.label || entry.value;
    return { value: entry.value, label };
  };

  next.campaign = resolveEntity(next.campaign, lists.campaigns, [
    "client_name",
    "name",
    "campaign_name",
  ]);
  next.designation = resolveEntity(next.designation, lists.designations, [
    "name",
    "designation_name",
  ]);
  next.office = resolveEntity(next.office, lists.offices, ["name", "office_name"]);

  if (next.status?.value) {
    const match = SETUP_STATUS_OPTIONS.find(
      (opt) => opt.value === String(next.status.value)
    );
    next.status = {
      value: next.status.value,
      label: match?.label || next.status.label || next.status.value,
    };
  } else {
    next.status = null;
  }

  return next;
}

export function rowMatchesSetupFilters(row, filters = EMPTY_SETUP_FILTERS) {
  if (!row) return false;

  if (filters?.campaign?.value) {
    const selectedId = String(filters.campaign.value);
    const campaigns = Array.isArray(row.campaigns) ? row.campaigns : [];
    const matched = campaigns.some((item) => String(item?.id) === selectedId);
    if (!matched) return false;
  }

  if (filters?.designation?.value) {
    const selectedId = String(filters.designation.value);
    const designations = Array.isArray(row.designations) ? row.designations : [];
    const matched = designations.some((item) => String(item?.id) === selectedId);
    if (!matched) return false;
  }

  if (filters?.office?.value) {
    const selectedId = String(filters.office.value);
    const officeId =
      row.office_id ?? row.office?.id ?? (row.office != null ? row.office : null);
    if (String(officeId || "") !== selectedId) return false;
  }

  if (filters?.status?.value) {
    const isPublished =
      row.is_published === 1 ||
      row.is_published === "1" ||
      row.is_published === true;
    if (filters.status.value === "published" && !isPublished) return false;
    if (filters.status.value === "draft" && isPublished) return false;
  }

  return true;
}

export function applySetupFilters(rows, filters = EMPTY_SETUP_FILTERS) {
  if (!rows?.length) return rows || [];
  if (!hasActiveSetupFilters(filters)) return rows;
  return rows.filter((row) => rowMatchesSetupFilters(row, filters));
}
